/*
 * THESIS: the scroll IS the journey; the visitor walks the snake path DOWN
 *   the Lean Pyramid, from purpose at the apex to value flowing at the base,
 *   exactly as the canon runs it, refusing panels, grids and drawers.
 * OWN-WORLD: Maestro graphite with the three layer accents washing the stage
 *   as you cross strata; every practice is a living instrument drawn in
 *   strokes, never an icon, never a text card.
 * STORY: a low-intent visitor descends through twenty-one working
 *   instruments, reaches the base where value flows, and the field manual is
 *   the reward; the lexicon, the essay and the letter wait past the base.
 * FIRST VIEWPORT: the claim, the trust line, and the descent cue; the first
 *   station one scroll away.
 * FORM: scrollytelling monument (pinned stage + travelling marker), chosen
 *   over the seeded card catalog after the founder rejected panel grammar;
 *   direction pinned by the founder's brief for movement. Journey descends,
 *   per the canon: start with purpose at the top, monitor at the base.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Seo from '../Seo'
import SiteAmbient from '../common/SiteAmbient'
import trackEvent from '../../utils/trackEvent'
import { getCanonContent } from '../../content/canonContent'
import { getInsightsContent } from '../../content/insightsContent'
import { CANON_SLUGS } from '../../content/canonSlugs'
import CtaClose from '../../components/common/CtaClose'
import INSTRUMENTS from './instruments'

const ACCENT = {
  1: { text: 'text-pyramid-1', line: 'bg-pyramid-1', border: 'border-pyramid-1/30', hex: '#c9d33a', wash: 'rgba(201, 211, 58, 0.04)' },
  10: { text: 'text-pyramid-10', line: 'bg-pyramid-10', border: 'border-pyramid-10/30', hex: '#f88116', wash: 'rgba(248, 129, 22, 0.045)' },
  19: { text: 'text-pyramid-19', line: 'bg-pyramid-19', border: 'border-pyramid-19/30', hex: '#19a2cf', wash: 'rgba(25, 162, 207, 0.045)' },
}

// Which /talk lane a practice conversation lands in, by layer.
const LAYER_LANE = { strategy: 'strategy', management: 'workshops', engineering: 'sdlc' }

const CONTACT_API = (process.env.REACT_APP_API_BASE_URL || 'https://app.leanit101.com')
  .replace(/\/$/, '')
  // plain http to the API dies in CORS preflight (nginx 301s to https)
  .replace(/^http:\/\/(?=[^/]*leanit101\.com)/, 'https://')

/*
 * The trail index: the descent as a typographic log. Twenty-one station
 * titles grouped by layer, the current one lit, the travelled ones in
 * their layer color; clicking a row rides the scroll to that station.
 * Deliberately no illustration; the commissioned pyramid artwork can take
 * this column later without touching the engine.
 */
const TrailIndex = ({ stations, idx, onJump }) => {
  let lastLayer = null
  return (
    <nav aria-label="The descent" className="space-y-1">
      {stations.map((s, i) => {
        const accent = ACCENT[s.layer.accent]
        const header = s.layer.id !== lastLayer
        lastLayer = s.layer.id
        const passed = i < idx
        const current = i === idx
        return (
          <div key={s.n}>
            {header ? (
              <p className={`mb-1 mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] first:mt-0 ${accent.text}`}>
                <span className={`h-1 w-1 rounded-full ${accent.line}`} />
                {s.layer.name}
              </p>
            ) : null}
            <button
              type="button"
              onClick={() => onJump(i)}
              aria-current={current ? 'step' : undefined}
              className={`group flex w-full items-baseline gap-3 rounded px-1.5 py-[3px] text-left transition-colors duration-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-white/50 ${
                current ? 'bg-white/[0.04]' : 'hover:bg-white/[0.02]'
              }`}
            >
              <span className={`w-6 shrink-0 text-right text-[11px] tabular-nums ${current ? accent.text : passed ? `${accent.text} opacity-60` : 'text-slate-600'}`}>
                {String(s.n).padStart(2, '0')}
              </span>
              <span className={`truncate text-[13px] transition-colors ${current ? 'font-medium text-white' : passed ? 'text-slate-400' : 'text-slate-500 group-hover:text-slate-300'}`}>
                {s.title}
              </span>
            </button>
          </div>
        )
      })}
    </nav>
  )
}

const InsightsPage = () => {
  const { i18n } = useTranslation()
  const c = getCanonContent(i18n.language)
  const essay = getInsightsContent(i18n.language)

  // flat, numbered station list, apex to base; slugs make stations linkable
  const stations = useMemo(() => {
    let n = 0
    return c.layers.flatMap((layer) =>
      layer.stations.map((s, i) => ({ ...s, n: ++n, layer, slug: (CANON_SLUGS[layer.id] || [])[i] }))
    )
  }, [c])
  const total = stations.length

  const [idx, setIdx] = useState(0)
  const journeyRef = useRef(null)
  const idxRef = useRef(0)
  const seenRef = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    // synchronous on purpose: cheap, and it must also run in background tabs
    let prevP = -1
    const onScroll = () => {
      const el = journeyRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const travel = rect.height - window.innerHeight
      const p = Math.min(1, Math.max(0, -rect.top / Math.max(1, travel)))
      if (p === prevP) return
      prevP = p
      // a small deadband so a 1px oscillation at a boundary cannot restart
      // the scene swap and every instrument animation
      const exact = p * total
      const next = Math.min(total - 1, Math.floor(exact))
      if (next !== idxRef.current) {
        const boundary = Math.max(next, idxRef.current)
        if (Math.abs(exact - boundary) > 0.06) {
          idxRef.current = next
          setIdx(next)
        }
      }
      if (!seenRef.current && p > 0.02) {
        seenRef.current = true
        trackEvent('canon_walk_start')
      }
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [total])

  const jumpTo = (i) => {
    const el = journeyRef.current
    if (!el || typeof window === 'undefined') return
    const rect = el.getBoundingClientRect()
    const travel = rect.height - window.innerHeight
    const top = window.scrollY + rect.top + travel * ((i + 0.55) / total)
    window.scrollTo({ top, behavior: 'smooth' })
    trackEvent('canon_trail_jump', { station: stations[i].title })
  }

  // Deep links: /insights#<station-slug> lands the descent on that station.
  // The stage is scroll-driven, so the hash resolves through the same math
  // the trail index uses. Retried over the first second because the browser's
  // own scroll restoration and late layout both fight the first attempt.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const indexOfHash = () => stations.findIndex((st) => st.slug === window.location.hash.slice(1))
    const attempt = (i) => {
      const el = journeyRef.current
      if (i < 0 || !el || window.innerHeight === 0) return
      const rect = el.getBoundingClientRect()
      const travel = rect.height - window.innerHeight
      if (travel <= 0) return
      const top = window.scrollY + rect.top + travel * ((i + 0.55) / total)
      if (Math.abs(window.scrollY - top) > 40) window.scrollTo({ top, behavior: 'auto' })
    }
    let timers = []
    if (indexOfHash() >= 0) {
      if (window.history && 'scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual'
      }
      timers = [60, 300, 900].map((ms) => setTimeout(() => attempt(indexOfHash()), ms))
      trackEvent('canon_deeplink', { station: stations[indexOfHash()].title })
    }
    const onHash = () => attempt(indexOfHash())
    window.addEventListener('hashchange', onHash)
    return () => {
      timers.forEach(clearTimeout)
      window.removeEventListener('hashchange', onHash)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const s = stations[idx]
  const accent = ACCENT[s.layer.accent]
  const Instrument = INSTRUMENTS[idx] || INSTRUMENTS[0]

  const [manualEmail, setManualEmail] = useState('')
  const [manualState, setManualState] = useState('idle')
  const [subEmail, setSubEmail] = useState('')
  const [subState, setSubState] = useState('idle')
  const [flippedTerms, setFlippedTerms] = useState({})
  // honeypot shared by the two capture forms; server drops when filled
  const [trap, setTrap] = useState('')

  // one debounced announcement per settle, instead of narrating every station
  // a fast scroll rushes past
  const [announce, setAnnounce] = useState('')
  useEffect(() => {
    const t = setTimeout(() => {
      const st = stations[idx]
      setAnnounce(`${String(st.n).padStart(2, '0')} / ${total} · ${st.title}`)
    }, 600)
    return () => clearTimeout(t)
  }, [idx, stations, total])

  const capture = async ({ email, source, message, setStatus, event }) => {
    const trimmed = email.trim()
    if (!trimmed) return
    setStatus('sending')
    const payload = {
      name: trimmed.split('@')[0],
      fullName: trimmed.split('@')[0],
      email: trimmed,
      workEmail: trimmed,
      message,
      notes: message,
      source,
      website: trap,
      formData: { email: trimmed, source },
      contact: { email: trimmed },
    }
    try {
      const res = await fetch(`${CONTACT_API}/api/contacts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error('failed')
      setStatus('done')
      trackEvent(event)
    } catch (err) {
      setStatus('error')
    }
  }

  return (
    <div className="relative isolate bg-transparent font-Quicksand text-white">
      <SiteAmbient />
      <Seo title={c.seo.title} description={c.seo.description} path="/insights" />

      {/* the apex: where the journey begins */}
      <header className="px-6 pt-32 md:px-16 md:pt-40">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs uppercase tracking-[0.4em] text-pyramid-19/80">{c.hero.kicker}</p>
          <h1 className="mt-4 max-w-3xl text-4xl font-light leading-[1.05] md:text-6xl">{c.hero.headline}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">{c.hero.body}</p>
          <p className="mt-8 max-w-2xl border-l-0 border-t border-white/[0.08] pt-5 text-[15px] leading-7 text-slate-400">
            <span className="mr-2 text-xs uppercase tracking-[0.3em] text-slate-400">{c.spine.apexLabel}</span>
            <span className="text-slate-300">{c.spine.apex}</span>
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <p className="motion-safe:animate-bounce text-2xl text-pyramid-10" aria-hidden="true">&#8595;</p>
            <a href="#base" className="text-sm text-slate-400 underline decoration-white/20 underline-offset-4 hover:text-slate-200">
              {c.climb.skip}
            </a>
          </div>
        </div>
      </header>

      {/* THE DESCENT */}
      <p aria-live="polite" className="sr-only">{announce}</p>
      <section ref={journeyRef} style={{ height: `${total * 62}vh` }} className="relative mt-10">
        <div className="climb-wash climb-stage sticky top-0 flex h-screen items-center overflow-hidden" style={{ backgroundColor: accent.wash }}>
          <div className="mx-auto grid w-full max-w-6xl items-center gap-8 px-6 md:px-16 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-14">
            <div className="hidden lg:block">
              <TrailIndex stations={stations} idx={idx} onJump={jumpTo} />
            </div>

            <div>
              {/* mobile progress: the descent line */}
              <div className="mb-6 flex items-center gap-3 lg:hidden">
                <svg viewBox="0 0 22 18" className="h-4 w-5" aria-hidden="true">
                  <path d="M11 1 L21 17 H1 Z" fill="none" stroke={accent.hex} strokeWidth="1.6" />
                </svg>
                <div className="h-px flex-1 bg-white/10">
                  <div className={`h-px ${accent.line}`} style={{ width: `${((idx + 1) / total) * 100}%` }} />
                </div>
              </div>

              <div key={idx} className="pane-swap is-visible">
                <p className={`text-xs uppercase tracking-[0.35em] ${accent.text}`}>
                  {s.layer.name}
                  <span className="ml-3 normal-case tracking-normal text-slate-400">{String(s.n).padStart(2, '0')} / {total}</span>
                </p>
                {idx === 0 || stations[idx - 1].layer.id !== s.layer.id ? (
                  <p className="mt-2 max-w-xl text-sm leading-6 text-slate-400">{s.layer.claim}</p>
                ) : null}
                <h2 className="mt-3 max-w-xl text-3xl font-light leading-tight md:text-[2.6rem]">{s.title}</h2>
                <span className={`title-line mt-4 block h-0.5 w-16 rounded-full ${accent.line}`} />
                <p className="mt-3 text-[15px] italic text-slate-400">{s.line}</p>

                <div className={`instrument mt-6 max-w-xl ${accent.text}`}>
                  <Instrument t={c.gfx} />
                </div>

                <p className="mt-6 max-w-xl font-Barlow text-[15.5px] leading-7 text-slate-300">{s.doctrine}</p>
                {s.terms ? (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {s.terms.map((t) => (
                      <span key={t} className={`rounded-full border px-3 py-1 text-xs ${accent.border} ${accent.text}`}>{t}</span>
                    ))}
                  </div>
                ) : null}
                <a
                  href={`/talk?i=${LAYER_LANE[s.layer.id] || 'discovery'}`}
                  onClick={() => trackEvent('canon_discuss_click', { station: s.title })}
                  className={`mt-5 inline-flex items-center gap-2 text-sm font-semibold ${accent.text}`}
                >
                  {c.meta.discuss}
                  <span aria-hidden="true">&#8594;</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE BASE: where value flows, and the loop restarts */}
      <section id="base" className="px-6 pt-24 md:px-16 md:pt-32">
        <div className="mx-auto max-w-6xl">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/90">{c.spine.baseLabel}</p>
            <h2 className="mx-auto mt-5 max-w-3xl text-3xl font-light leading-tight md:text-5xl">{c.spine.base}</h2>
            <p className="mx-auto mt-6 max-w-2xl leading-8 text-slate-400">{c.climb.loop}</p>
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {c.spine.principles.map((p) => (
                <span key={p} className="rounded-full border border-white/[0.12] px-3.5 py-1.5 text-[13px] text-slate-300">{p}</span>
              ))}
            </div>
          </div>

          {/* the reward */}
          <section className="mx-auto mt-14 max-w-2xl rounded-2xl border border-pyramid-10/25 bg-pyramid-10/[0.05] p-7 text-center md:p-10">
            <p className="text-xs uppercase tracking-[0.3em] text-pyramid-10">{c.manual.kicker}</p>
            <h3 className="mt-3 text-2xl font-light">{c.manual.title}</h3>
            <p className="mx-auto mt-3 max-w-xl leading-7 text-slate-300">{c.manual.body}</p>
            {manualState === 'done' ? (
              <p className="pane-swap mx-auto mt-6 max-w-md rounded-lg border border-pyramid-1/40 bg-pyramid-1/10 px-4 py-3 text-sm font-semibold text-pyramid-1">
                {c.manual.done}
              </p>
            ) : (
              <form
                onSubmit={(e) =>
                 {
                  e.preventDefault()
                  capture({
                    email: manualEmail,
                    source: 'canon-manual-waitlist',
                    message: 'Field manual waitlist (the canon)',
                    setStatus: setManualState,
                    event: 'funnel_manual_waitlist',
                  })
                }}
                className="mx-auto mt-6 flex max-w-md flex-col gap-3 sm:flex-row"
              >
                <input type='text' name='website' value={trap} onChange={(e) => setTrap(e.target.value)} tabIndex={-1} autoComplete='off' aria-hidden='true' style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} />
                <input
                  type="email"
                  required
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  placeholder={c.manual.emailPlaceholder}
                  aria-label={c.manual.emailPlaceholder}
                  disabled={manualState === 'sending'}
                  className="w-full rounded-lg border border-white/[0.12] bg-[#151b23] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 focus:border-pyramid-10/60 focus:outline-none focus:ring-2 focus:ring-pyramid-10/20"
                />
                <button type="submit" disabled={manualState === 'sending'} className="btn-board btn-board-orange shrink-0">
                  {manualState === 'sending' ? c.manual.sending : c.manual.cta}
                </button>
              </form>
            )}
            {manualState === 'error' ? (
              <p role="alert" className="mt-3 text-sm text-pyramid-10">{c.manual.error}</p>
            ) : null}
          </section>
        </div>
      </section>

      <main className="px-6 pb-24 pt-20 md:px-16">
        <div className="mx-auto max-w-6xl">
          {/* the lexicon */}
          <section>
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">{c.lexicon.kicker}</p>
            <h2 className="mt-3 text-2xl font-light md:text-3xl">{c.lexicon.title}</h2>
            <p className="mt-2 text-sm text-slate-400">{c.lexicon.hint}</p>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {c.lexicon.terms.map((t, i) => {
                const hex = [ACCENT[19], ACCENT[10], ACCENT[1]][i % 3].hex
                const flipped = !!flippedTerms[t.term]
                return (
                  <button
                    key={t.term}
                    type="button"
                    onClick={() => setFlippedTerms((f) => ({ ...f, [t.term]: !f[t.term] }))}
                    aria-pressed={flipped}
                    className={`flip-card min-h-[10rem] w-full text-left ${flipped ? 'is-flipped' : ''}`}
                  >
                    <span className="flip-inner min-h-[10rem] w-full">
                      <span className="flip-face flex min-h-[10rem] flex-col rounded-xl border border-white/[0.09] bg-white/[0.02] p-4">
                        <span className="text-lg font-light text-white">{t.term}</span>
                        <span className="mt-1 text-xs text-slate-400">{t.gloss}</span>
                        <span className="mt-auto text-lg" style={{ color: hex }} aria-hidden="true">&#10549;</span>
                      </span>
                      <span className="flip-face flip-back overflow-hidden rounded-xl border p-4" style={{ borderColor: `${hex}40`, background: '#151b23' }}>
                        <span className="block text-[12.5px] leading-5 text-slate-300">{t.def}</span>
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          </section>

          {/* the essay */}
          <article className="mt-20 rounded-2xl border border-white/10 bg-white/[0.02] p-7 md:p-12">
            <p className="text-xs uppercase tracking-[0.3em] text-pyramid-10">{essay.article.kicker}</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-light md:text-4xl">{essay.article.title}</h2>
            <p className="mt-4 text-sm text-slate-400">
              <span className="font-semibold text-white">{essay.article.author}</span>
              <span className="mx-2" aria-hidden="true">&middot;</span>
              {essay.article.role}
            </p>
            <div className="mt-8 max-w-[70ch] space-y-6 font-Barlow text-[1.05rem] leading-8 text-slate-200">
              {essay.article.paragraphs.map((p) => (
                <p key={p.slice(0, 40)}>{p}</p>
              ))}
            </div>
            <a href={essay.article.readMoreHref} onClick={() => trackEvent('insights_readmore_click')} className="btn-board btn-board-orange mt-8">
              {essay.article.readMore}
              <span aria-hidden="true">&#8594;</span>
            </a>
          </article>

          {/* the letter */}
          <section className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-7 md:p-9">
            <div className="md:flex md:items-end md:justify-between md:gap-10">
              <div className="max-w-xl">
                <h3 className="text-xl font-semibold">{essay.follow.title}</h3>
                <p className="mt-3 leading-7 text-slate-400">{essay.follow.body}</p>
              </div>
              <div className="mt-6 w-full max-w-md md:mt-0">
                {subState === 'done' ? (
                  <p className="pane-swap rounded-lg border border-pyramid-1/40 bg-pyramid-1/10 px-4 py-3 text-sm font-semibold text-pyramid-1">
                    {essay.follow.done}
                  </p>
                ) : (
                  <form
                    onSubmit={(e) =>
                 {
                      e.preventDefault()
                      capture({
                        email: subEmail,
                        source: 'insights-newsletter',
                        message: 'Insights newsletter subscription',
                        setStatus: setSubState,
                        event: 'funnel_newsletter_subscribe',
                      })
                    }}
                    className="flex flex-col gap-3 sm:flex-row"
                  >
                    <input type='text' name='website' value={trap} onChange={(e) => setTrap(e.target.value)} tabIndex={-1} autoComplete='off' aria-hidden='true' style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} />
                    <input
                      type="email"
                      required
                      value={subEmail}
                      onChange={(e) => setSubEmail(e.target.value)}
                      placeholder={essay.follow.emailPlaceholder}
                      aria-label={essay.follow.emailPlaceholder}
                      disabled={subState === 'sending'}
                      className="w-full rounded-lg border border-white/[0.12] bg-[#151b23] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 focus:border-pyramid-19/60 focus:outline-none focus:ring-2 focus:ring-pyramid-19/20"
                    />
                    <button
                      type="submit"
                      disabled={subState === 'sending'}
                      className="shrink-0 rounded-lg border border-pyramid-19/40 bg-pyramid-19/10 px-6 py-3 text-sm font-semibold text-pyramid-19 transition-all hover:bg-pyramid-19/20 disabled:opacity-60"
                    >
                      {subState === 'sending' ? essay.follow.sending : essay.follow.cta}
                    </button>
                  </form>
                )}
                {subState === 'error' ? (
                  <p role="alert" className="mt-3 text-sm text-pyramid-10">{essay.follow.error}</p>
                ) : null}
              </div>
            </div>
          </section>
        </div>
      </main>

      <CtaClose lane="discovery" page="insights" />
    </div>
  )
}

export default InsightsPage
